import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ROTULO_TIPO_RELATORIO, type TipoRelatorio } from '@via-tax/contracts'
import { Aviso, Botao, EstadoVazio, Selo } from '@via-tax/ui'
import type { Tela } from '../components/Shell'
import { dataBr } from '../formatos'
import type { ContextoSessao } from '../tipos'
import { mensagemDeErro, useTRPC } from '../trpc'

/**
 * Relatórios (F9 e ADR 0024): da mesma simulação concluída saem três documentos — o Resumo de três
 * páginas para o cliente, o relatório técnico do escritório e a carta que o cliente encaminha a quem
 * compra dele. O escritório decide qual vai para o Portal do Cliente; o sugerido é o Resumo.
 */

const ROTULO_PORTAL = {
  NAO_PUBLICADO: { texto: 'não publicado', tom: 'neutro' },
  PUBLICANDO: { texto: 'enviando ao Portal…', tom: 'navy' },
  PUBLICADO: { texto: 'no Portal do Cliente', tom: 'ok' },
  ERRO: { texto: 'falha no Portal', tom: 'alerta' },
} as const

const quando = (d: string | Date | null) => {
  if (!d) return '—'
  const x = typeof d === 'string' ? new Date(d) : d
  return x.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

export function Relatorios({ contexto, ir }: { contexto: ContextoSessao; ir: (t: Tela) => void }) {
  const trpc = useTRPC()
  const qc = useQueryClient()
  const capacidades = contexto.capacidades as string[]
  const podeGerar = capacidades.includes('gerar_relatorio')
  const podePublicar = capacidades.includes('publicar_relatorio')
  const lista = useQuery({
    ...trpc.relatorio.listar.queryOptions(),
    refetchInterval: (q) => (q.state.data?.relatorios.some((r) => r.status === 'PROCESSANDO' || r.portalStatus === 'PUBLICANDO') ? 2500 : false),
  })
  const atualizar = { onSuccess: () => qc.invalidateQueries({ queryKey: trpc.relatorio.pathKey() }) }
  const gerar = useMutation(trpc.relatorio.gerar.mutationOptions(atualizar))
  const publicar = useMutation(trpc.relatorio.publicar.mutationOptions(atualizar))
  const retirar = useMutation(trpc.relatorio.despublicar.mutationOptions(atualizar))
  const erro = gerar.error ?? publicar.error ?? retirar.error
  const d = lista.data

  return (
    <div className="flex flex-col gap-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Relatórios</h1>
          <p className="mt-1 max-w-2xl text-sm text-tinta-2">
            Três documentos da última simulação: o <strong>Resumo para o cliente</strong> (3 páginas: a resposta, o preço e os próximos passos), o <strong>relatório técnico</strong>{' '}
            do escritório e a <strong>carta ao comprador</strong>. Simulação para decisão, não apuração.
          </p>
        </div>
        {podeGerar && (
          <div className="flex flex-wrap gap-2">
            <Botao variante="primario" onClick={() => gerar.mutate({ tipo: 'RESUMO' })} disabled={gerar.isPending}>
              Resumo para o cliente
            </Botao>
            <Botao onClick={() => gerar.mutate({ tipo: 'TECNICO' })} disabled={gerar.isPending}>
              Relatório técnico
            </Botao>
            <Botao variante="fantasma" onClick={() => gerar.mutate({ tipo: 'CARTA' })} disabled={gerar.isPending} title="Uma página para o cliente encaminhar a quem compra dele">
              Carta ao comprador
            </Botao>
          </div>
        )}
      </header>

      {erro && <Aviso>{mensagemDeErro(erro)}</Aviso>}
      {gerar.data?.reaproveitado && <Aviso tom="info">Essa simulação já tem esse documento — ele está na lista. Para um número novo, rode a simulação de novo antes.</Aviso>}
      {d && podePublicar && !d.portalConfigurado && <Aviso tom="info">A publicação no Portal do Cliente ainda não está configurada neste servidor.</Aviso>}

      {lista.isPending ? (
        <p className="text-sm text-tinta-3">Carregando…</p>
      ) : !d?.relatorios.length ? (
        <EstadoVazio
          titulo="Nenhum relatório ainda"
          acao={
            podeGerar ? (
              <Botao onClick={() => ir('foco2027')} variante="fantasma">
                Ir para a simulação
              </Botao>
            ) : undefined
          }
        >
          {podeGerar ? 'Os documentos saem de uma simulação concluída. Rode a simulação no Foco 2027 e gere aqui.' : 'O escritório ainda não gerou nenhum documento desta empresa.'}
        </EstadoVazio>
      ) : (
        <ul className="flex flex-col gap-3">
          {d.relatorios.map((r) => {
            const portal = ROTULO_PORTAL[r.portalStatus]
            const abaixoLimiar = r.mesesBase < d.limiarMeses
            return (
              <li key={r.id} className="rounded-lg border border-linha bg-painel px-5 py-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold">
                      {ROTULO_TIPO_RELATORIO[r.tipo as TipoRelatorio] ?? 'Relatório'}
                      {r.contraparteNome ? ` · ${r.contraparteNome}` : ''} · simulação de {dataBr(r.simulacaoEm)}
                    </p>
                    <p className="mt-0.5 text-xs text-tinta-3">
                      pedido por {r.criadoPor} em {quando(r.criadoEm)} · base com {r.mesesBase} mês(es) · parâmetros {r.versaoParametros ?? '—'}
                      {r.paginas ? ` · ${r.paginas} páginas` : ''}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {r.status === 'PROCESSANDO' && <Selo tom="navy">gerando…</Selo>}
                    {r.status === 'ERRO' && <Selo tom="alerta">falhou</Selo>}
                    {r.status === 'CONCLUIDO' && <Selo tom={portal.tom}>{portal.texto}</Selo>}
                    {abaixoLimiar && <Selo tom="alerta">base parcial</Selo>}
                  </div>
                </div>
                {r.status === 'ERRO' && <p className="mt-2 text-sm text-alerta">{r.erro}</p>}
                {r.portalStatus === 'ERRO' && <p className="mt-2 text-sm text-alerta">{r.portalMensagem}</p>}
                {r.status === 'CONCLUIDO' && (
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <a href={`/relatorios/${r.id}/pdf`} target="_blank" rel="noreferrer">
                      <Botao>Abrir PDF</Botao>
                    </a>
                    {podePublicar && d.portalConfigurado && r.portalStatus !== 'PUBLICADO' && (
                      <Botao
                        onClick={() => publicar.mutate({ relatorioId: r.id })}
                        disabled={publicar.isPending || r.portalStatus === 'PUBLICANDO' || abaixoLimiar}
                        title={
                          abaixoLimiar
                            ? `Base com menos de ${d.limiarMeses} meses: complete os XMLs antes de publicar`
                            : r.tipo === 'RESUMO'
                              ? 'Envia este Resumo para o Portal do Cliente'
                              : 'Envia este documento para o Portal do Cliente — o sugerido para o cliente é o Resumo'
                        }
                      >
                        Publicar no Portal
                      </Botao>
                    )}
                    {podePublicar && d.portalConfigurado && r.portalStatus === 'PUBLICADO' && (
                      <Botao variante="fantasma" onClick={() => retirar.mutate({ relatorioId: r.id })} disabled={retirar.isPending}>
                        Retirar do Portal
                      </Botao>
                    )}
                    {r.publicadoEm && <span className="text-xs text-tinta-3">publicado em {quando(r.publicadoEm)}</span>}
                    {abaixoLimiar && podePublicar && (
                      <span className="text-xs text-alerta">
                        Base com {r.mesesBase} mês(es): publicar no Portal exige {d.limiarMeses}.
                      </span>
                    )}
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
